import { FaroTextButton } from "@components/common/faro-text-button";
import { DropdownButton } from "@components/dropdown/dropdown-button";
import { green, Text } from "@flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Stack, SvgIcon } from "@mui/material";
import { useUserContext } from "@pages/account-settings/user-provider";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { withEllipsisTwoLines } from "@styles/common-styles";

interface Props {
  /** Unique identifier of the integration, used to retrieve integration data and perform specific actions. */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /**
   * Set containing the IDs of integrations currently enabled.
   * Used to check if the current integration is enabled.
   */
  enabledIntegrationIds: Set<FaroCoreApiTypes.IntegrationId>;

  /** Called when integration should be enabled */
  onEnable(integrationId: FaroCoreApiTypes.IntegrationId): void;

  /** Called when integration should be disabled */
  onDisable(integrationId: FaroCoreApiTypes.IntegrationId): void;
}

/** Component that renders a single row representing an integration. */
export function IntegrationRow({
  integrationId,
  enabledIntegrationIds,
  onEnable,
  onDisable,
}: Props): JSX.Element {
  const integration = INTEGRATION_INFO[integrationId];
  const isEnabled = enabledIntegrationIds.has(integrationId);
  const { user } = useUserContext();
  assert(user, "User has to be defined to show email");

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      paddingX="1.25rem"
      justifyContent="space-between"
      width="100%"
      gap="1rem"
      marginY="1.75rem"
    >
      <Stack flexDirection="row" gap="1.875rem" alignItems="center">
        <SvgIcon
          inheritViewBox={true}
          component={integration.icon}
          sx={{ width: "2.5rem", height: "2.5rem" }}
        />
        <Stack>
          <Text variant="heading20">{integration.displayName}</Text>
          <Text
            variant="bodyS"
            marginTop="0.25rem"
            title={integration.description}
            sx={{ ...withEllipsisTwoLines }}
          >
            {isEnabled ? <var>{user.email}</var> : integration.description}
          </Text>
        </Stack>
      </Stack>

      {isEnabled ? (
        <DropdownButton
          label="Connected"
          statusChipColor={green[600]}
          actions={[
            {
              label: "Disconnect...",
              onClick: () => onDisable(integrationId),
            },
          ]}
        />
      ) : (
        <FaroTextButton onClick={() => onEnable(integrationId)}>
          Connect
        </FaroTextButton>
      )}
    </Stack>
  );
}
